import {createReducer, on} from '@ngrx/store';
import {
  AddSurveyResponseFailure,
  AddSurveyResponseRequest,
  AddSurveyResponseSuccess,
  AddSurveySendScheduleDataFailure,
  AddSurveySendScheduleDataRequest,
  AddSurveySendScheduleDataSuccess,
  ClearState,
  CloseSurveyFailure,
  CloseSurveyRequest,
  CloseSurveySuccess,
  CreateDuplicateSurveyFailure,
  CreateDuplicateSurveyRequest,
  CreateDuplicateSurveySuccess,
  EditSurveyDataForEditScreenFailure,
  EditSurveyDataForEditScreenRequest,
  EditSurveyDataForEditScreenSuccess,
  EditSurveySendScheduleDataFailure,
  EditSurveySendScheduleDataRequest,
  EditSurveySendScheduleDataSuccess,
  FetchClinicsOptionsListFailure,
  FetchClinicsOptionsListRequest,
  FetchClinicsOptionsListSuccess,
  FetchEditSurveySummaryDetailsFailure,
  FetchEditSurveySummaryDetailsRequest,
  FetchEditSurveySummaryDetailsSuccess,
  FetchGenderOptionsListFailure,
  FetchGenderOptionsListRequest,
  FetchGenderOptionsListSuccess,
  FetchGroupsOptionsListFailure,
  FetchGroupsOptionsListRequest,
  FetchGroupsOptionsListSuccess,
  FetchGroupStatusOptionsListFailure,
  FetchGroupStatusOptionsListRequest,
  FetchGroupStatusOptionsListSuccess,
  FetchManageSurveyListFailure,
  FetchManageSurveyListRequest,
  FetchManageSurveyListSuccess,
  FetchMyQuestionnairesDetailsFailure,
  FetchMyQuestionnairesDetailsRequest,
  FetchMyQuestionnairesDetailsSuccess,
  FetchMyQuestionnairesListFailure,
  FetchMyQuestionnairesListRequest,
  FetchMyQuestionnairesListSuccess,
  FetchQuestionnaireDetailsForEditViewFailure,
  FetchQuestionnaireDetailsForEditViewRequest,
  FetchQuestionnaireDetailsForEditViewSuccess,
  FetchQuestionnairesListFailure,
  FetchQuestionnairesListRequest,
  FetchQuestionnairesListSuccess,
  FetchReviewSurveyDetailsFailure,
  FetchReviewSurveyDetailsRequest,
  FetchReviewSurveyDetailsSuccess,
  FetchRolesOptionsListFailure,
  FetchRolesOptionsListRequest,
  FetchRolesOptionsListSuccess,
  FetchScheduleAccordingToListForCreateSurveyFailure,
  FetchScheduleAccordingToListForCreateSurveyRequest,
  FetchScheduleAccordingToListForCreateSurveySuccess,
  FetchSessionListForCreateSurveyFailure,
  FetchSessionListForCreateSurveyRequest,
  FetchSessionListForCreateSurveySuccess,
  FetchSurveyDetailsByIdFailure,
  FetchSurveyDetailsByIdRequest,
  FetchSurveyDetailsByIdSuccess,
  FetchSurveyResponseForReviewAndEditForSuperAdminFailure,
  FetchSurveyResponseForReviewAndEditForSuperAdminRequest,
  FetchSurveyResponseForReviewAndEditForSuperAdminSuccess,
  FetchPendingSurveyCountRequest,
  FetchPendingSurveyCountSuccess,
  FetchPendingSurveyCountFailure,
} from "./questionnaires.actions";
import {QuestionnaireModel} from "../../../../models/questionnaire.model";
import {DropdownOption} from "../../../../models/dropdown-option.model";
import {
  FetchSurveyForDuplicateDetails,
  ManageSurveyItem,
  MyQuestionnaireModel,
  QuestionnaireDetails,
  RespondentModel,
  SurveyReviewDetails
} from "../models/fetch-questionnaires-list.model";

// State for this feature (User)
export interface QuestionnairesState {
  questionnairesList: {
    list: QuestionnaireModel[],
    loading: boolean
  },
  createSurvey?: {
    session?: {
      list?: DropdownOption[],
      loading?: boolean
    },
    scheduleAccordingToDetails?: {
      list?: DropdownOption[],
      loading?: boolean
    },
    survey?: any
  },
  myQuestionnairesList?: {
    list?: MyQuestionnaireModel[],
    loading?: boolean;
    hasNext?: boolean, hasPrev?: boolean, limit?: number, pageNumber?: number, total?: number
  }
  myQuestionnairesDetails?: {
    loading: boolean,
    survey: QuestionnaireDetails
  },
  surveyReview?: {
    loading?: boolean,
    surveyReviewDetails?: SurveyReviewDetails
  },
  surveyResponseReview?: {
    loading?: boolean,
    surveyReviewDetails?: SurveyReviewDetails
  },
  editSurvey?: {
    surveyDetails?: {
      loading?: boolean,
      details: QuestionnaireDetails
    },
    summaryDetails?: {
      loading?: boolean,
      summary: {
        respondent: RespondentModel[], pending: RespondentModel[]
      }
    }
  },
  surveyDuplicate: FetchSurveyForDuplicateDetails['response']['data'],
  manageSurveyList: {
    details: {
      list: ManageSurveyItem[],
      hasNext?: boolean,
      hasPrev?: boolean,
      limit?: number,
      pageNumber?: number,
      total?: number
    }, loading: boolean
  },
  sendToFilters: {
    roles: {
      loading: boolean,
      list: DropdownOption[],
    },
    gender: {
      loading: boolean,
      list: DropdownOption[]
    },
    clinics: {
      loading: boolean,
      list: DropdownOption[],
      hasNext?: boolean
      hasPrev?: boolean
      limit?: number,
      pageNumber?: number
      total?: number
    },
    groupStatus: {
      loading: boolean,
      list: DropdownOption[]
    },
    groups: {
      loading: boolean,
      list: DropdownOption[],
      hasNext?: boolean
      hasPrev?: boolean
      limit?: number,
      pageNumber?: number
      total?: number
    }
  },
  surveyDetails: QuestionnaireDetails,
  pendingSurveyCount: {
    count: number,
    loading: boolean
  }
}

const initialState: QuestionnairesState = {
  questionnairesList: {
    list: [],
    loading: false
  },
  createSurvey: {
    session: {
      list: [],
      loading: false
    },
    scheduleAccordingToDetails: {
      list: [],
      loading: false
    },
    survey: {},
  },
  myQuestionnairesList: {
    list: [],
    loading: false,
    hasNext: false,
    hasPrev: false,
    pageNumber: 0,
    total: 0,
    limit: 0
  },
  myQuestionnairesDetails: {
    survey: {},
    loading: false
  },
  surveyReview: {
    surveyReviewDetails: {
      description: '',
      title: '',
      value: [],
      surveyId: ''
    },
    loading: false
  },
  surveyResponseReview: {
    surveyReviewDetails: {
      description: '',
      title: '',
      value: [],
      surveyId: ''
    },
    loading: false
  },
  editSurvey: {
    surveyDetails: {
      details: {},
      loading: false
    },
    summaryDetails: {
      summary: {
        pending: [], respondent: []
      },
      loading: false
    }
  },
  surveyDuplicate: {
    schedule: {},
    surveyForm: {},
    sendTo: {}
  },
  manageSurveyList: {
    details: {hasPrev: false, list: [], hasNext: false, total: 0, limit: 0, pageNumber: 0},
    loading: false
  },
  sendToFilters: {
    roles: {
      loading: false,
      list: [],
    },
    gender: {
      loading: false,
      list: []
    },
    clinics: {
      loading: false,
      list: [],
      hasNext: false,
      hasPrev: false,
      limit: 0,
      pageNumber: 0,
      total: 0
    },
    groupStatus: {
      loading: false,
      list: []
    },
    groups: {
      loading: false,
      list: [],
      hasNext: false,
      hasPrev: false,
      limit: 0,
      pageNumber: 0,
      total: 0
    }
  },
  surveyDetails: {},
  pendingSurveyCount: {
    count: 0,
    loading: false
  }
};

export const reducer = createReducer(
  initialState,


  // FETCH QUESTIONNAIRES LIST
  on(FetchQuestionnairesListRequest, (state) => ({
    ...state,
    questionnairesList: {
      ...initialState.questionnairesList,
      loading: true
    }
  })),
  on(FetchQuestionnairesListSuccess, (state, props) => ({
    ...state,
    questionnairesList: {
      list: props.questionnairesList,
      loading: false
    }
  })),
  on(FetchQuestionnairesListFailure, (state) => ({
    ...state,
    questionnairesList: {
      ...initialState.questionnairesList,
      loading: false
    }
  })),

//fetch my questionnaires
  on(FetchMyQuestionnairesListRequest, (state, props) => {
    return ({
      ...state, myQuestionnairesList: {
        ...state.myQuestionnairesList, list: [...(props.payload.pageNumber ? props.payload.pageNumber === 1 ? [] : [...state.myQuestionnairesList.list] : [])], loading: true
      }
    })
  }),

  on(FetchMyQuestionnairesListSuccess, (state, props) => {
    return ({
      ...state, myQuestionnairesList: {
        ...props.payload, list: [
          ...(props.payload.pageNumber === 1 ? [...props.payload.list] : [
            ...state.myQuestionnairesList.list, ...props.payload.list
          ])
        ], loading: false
      }
    })
  }),

  on(FetchMyQuestionnairesListFailure, (state) => ({...state, myQuestionnairesList: {list: [], loading: false}})),

  on(FetchMyQuestionnairesDetailsRequest, (state) => ({
    ...state, myQuestionnairesDetails: {
      survey: {
        descriptions: '', title: '', formData: []
      }, loading: true
    }
  })),

  on(FetchMyQuestionnairesDetailsSuccess, (state, props) => ({
    ...state, myQuestionnairesDetails: {
      survey: props.payload.details, loading: false
    }
  })),

  on(FetchMyQuestionnairesDetailsFailure, (state) => ({
    ...state, myQuestionnairesDetails: {
      survey: {
        descriptions: '', title: '', formData: []
      }, loading: false
    }
  })),

  //fetch survey details
  on(FetchReviewSurveyDetailsRequest, (state, props) => ({
    ...state, surveyReview: {
      surveyReviewDetails: {
        description: '', title: '', value: [], surveyId: ''
      }, loading: true
    }
  })),

  on(FetchReviewSurveyDetailsSuccess, (state, props) => ({
    ...state, surveyReview: {
      surveyReviewDetails: {...props.payload.surveyDetails}, loading: false
    }
  })),

  on(FetchReviewSurveyDetailsFailure, (state, props) => ({
    ...state, surveyReview: {
      surveyReviewDetails: {
        description: '', title: '', value: [], surveyId: ''
      }, loading: false
    }
  })),

//fetch edit details
  on(FetchEditSurveySummaryDetailsRequest, (state) => {
    return ({
      ...state, editSurvey: {
        ...state.editSurvey, summaryDetails: {summary: {pending: [], respondent: []}, loading: true}
      }
    })
  }),

  on(FetchEditSurveySummaryDetailsSuccess, (state, props) => {
    return ({
      ...state, editSurvey: {
        ...state.editSurvey, summaryDetails: {summary: {pending: props.payload.pending?.length > 0 ? [...props.payload.pending] : [], respondent: props.payload.respondents?.length > 0 ? [...props.payload.respondents] : []}, loading: false}
      }
    })
  }),

  on(FetchEditSurveySummaryDetailsFailure, (state) => {
    return ({
      ...state, editSurvey: {
        ...state.editSurvey, summaryDetails: {summary: {pending: [], respondent: []}, loading: false}
      }
    })
  }),

//TODO: Todo
  on(FetchManageSurveyListRequest, (state, props) => {
    return ({
      ...state, manageSurveyList: {
        details: {
          ...state.manageSurveyList.details, list: [
            ...(props.payload?.pageNumber ?
                props.payload?.pageNumber === 1 ?
                  [] :
                  [...state.manageSurveyList.details.list] :
              [])
          ]
        },
        loading: true
      }
    })
  }),

  on(FetchManageSurveyListSuccess, (state, props) => {
    return ({
      ...state, manageSurveyList: {
        details: {
          ...props.payload, list: props.payload.pageNumber === 1 ? [...props.payload.list] : [
            ...state.manageSurveyList.details.list, ...props.payload.list
          ]
        },
        loading: false
      }
    })
  }),

  on(FetchManageSurveyListFailure, (state) => ({
    ...state, manageSurveyList: {
      details: {
        list: [], hasNext: false, pageNumber: 0, limit: 0, total: 0, hasPrev: false
      },
      loading: false
    }
  })),

//session list
  on(FetchSessionListForCreateSurveyRequest, (state) => ({
    ...state, createSurvey: {
      ...state.createSurvey, session: {
        list: [],
        loading: true
      }
    }
  })),

  on(FetchSessionListForCreateSurveySuccess, (state, props) => ({
    ...state, createSurvey: {
      ...state.createSurvey, session: {
        list: [...props.payload.sessionList],
        loading: false
      }
    }
  })),

  on(FetchSessionListForCreateSurveyFailure, (state) => ({
    ...state, createSurvey: {
      ...state.createSurvey, session: {
        list: [],
        loading: false
      }
    }
  })),

//schedule according to list
  on(FetchScheduleAccordingToListForCreateSurveyRequest, (state) => ({
    ...state, createSurvey: {
      ...state.createSurvey, scheduleAccordingToDetails: {
        list: [], loading: true
      }
    }
  })),

  on(FetchScheduleAccordingToListForCreateSurveySuccess, (state, props) => ({
    ...state, createSurvey: {
      ...state.createSurvey, scheduleAccordingToDetails: {
        list: [...props.payload.scheduleAccordingToList], loading: false
      }
    }
  })),

  on(FetchScheduleAccordingToListForCreateSurveyFailure, (state) => ({
    ...state, createSurvey: {
      ...state.createSurvey, scheduleAccordingToDetails: {
        list: [], loading: false
      }
    }
  })),

  //create survey send schedule details
  on(AddSurveySendScheduleDataRequest, (state) => state),

  on(AddSurveySendScheduleDataSuccess, (state, props) => ({...state, createSurvey: {...state.createSurvey, survey: props.payload}})),

  on(AddSurveySendScheduleDataFailure, (state) => state),

//fetch roles list
  on(FetchRolesOptionsListRequest, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, roles: {
        loading: true, list: []
      }
    }
  })),

  on(FetchRolesOptionsListSuccess, (state, props) => {
    return ({
      ...state, sendToFilters: {
        ...state.sendToFilters, roles: {loading: false, list: props.payload.list}
      }
    })
  }),

  on(FetchRolesOptionsListFailure, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, roles: {
        loading: false, list: []
      }
    }
  })),

//fetch gender

  on(FetchGenderOptionsListRequest, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, gender: {
        loading: true, list: []
      }
    }
  })),

  on(FetchGenderOptionsListSuccess, (state, props) => {
    return ({
      ...state, sendToFilters: {
        ...state.sendToFilters, gender: {loading: false, list: props.payload.list}
      }
    })
  }),

  on(FetchGenderOptionsListFailure, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, gender: {
        loading: false, list: []
      }
    }
  })),

//fetch clinics
  on(FetchClinicsOptionsListRequest, (state, props) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, clinics: {
        loading: true, list: [
          ...(props.payload?.pageNumber ?
              props.payload?.pageNumber === 1 ?
                [] :
                [...state.sendToFilters.clinics.list] :
            [])
        ]
      }
    }
  })),

  on(FetchClinicsOptionsListSuccess, (state, props) => {
    return ({
      ...state, sendToFilters: {
        ...state.sendToFilters, clinics: {
          ...props.payload,
          loading: false,
          list: [
            ...(props.payload?.pageNumber ?
                props.payload?.pageNumber === 1 ?
                props.payload.list :
                  [...state.sendToFilters.clinics.list, ...props.payload.list] :
              [])
          ]
        }
      }
    })
  }),

  on(FetchClinicsOptionsListFailure, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, clinics: {
        loading: false, list: []
      }
    }
  })),

//fetch group status
  on(FetchGroupStatusOptionsListRequest, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, groupStatus: {
        loading: true, list: []
      }
    }
  })),

  on(FetchGroupStatusOptionsListSuccess, (state, props) => {
    return ({
      ...state, sendToFilters: {
        ...state.sendToFilters, groupStatus: {loading: false, list: props.payload.list}
      }
    })
  }),

  on(FetchGroupStatusOptionsListFailure, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, groupStatus: {
        loading: false, list: []
      }
    }
  })),


//fetch group list
  on(FetchGroupsOptionsListRequest, (state, props) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, groups: {
        loading: true, list: [
          ...(props.payload?.pageNumber ?
              props.payload?.pageNumber === 1 ?
                [] :
                [...state.sendToFilters.clinics.list] :
            [])
        ],
        ...state.sendToFilters.groups
      }
    }
  })),

  on(FetchGroupsOptionsListSuccess, (state, props) => {
    return ({
      ...state, sendToFilters: {
        ...state.sendToFilters, groups: {
          hasPrev: props.payload.hasPrev,
          loading: false,
          list: [
            ...(props.payload?.pageNumber ?
                props.payload?.pageNumber === 1 ?
                props.payload.list :
                  [...state.sendToFilters.groups.list, ...props.payload.list] :
              [])
          ],
          hasNext: props.payload.hasNext,
          total: props.payload.total,
          limit: props.payload.limit,
          pageNumber: props.payload.pageNumber
        }
      }
    })
  }),

  on(FetchGroupsOptionsListFailure, (state) => ({
    ...state, sendToFilters: {
      ...state.sendToFilters, groups: {
        loading: false, list: []
      }
    }
  })),

  //fetch surveyDetails
  on(FetchSurveyDetailsByIdRequest, state => ({...state, surveyDetails: {}})),

  on(FetchSurveyDetailsByIdSuccess, (state, props) => ({...state, surveyDetails: {...props.payload.surveyDetails}})),

  on(FetchSurveyDetailsByIdFailure, state => ({...state, surveyDetails: {}})),

//edit Survey
  on(EditSurveySendScheduleDataRequest, state => state),

  on(EditSurveySendScheduleDataSuccess, state => state),

  on(EditSurveySendScheduleDataFailure, state => state),

//add survey
  on(AddSurveySendScheduleDataRequest, state => state),

  on(AddSurveySendScheduleDataSuccess, state => state),

  on(AddSurveySendScheduleDataFailure, state => state),

//duplicate survey
  on(CreateDuplicateSurveyRequest, state => state),

  on(CreateDuplicateSurveySuccess, state => state),

  on(CreateDuplicateSurveyFailure, state => state),

//fetch details for edit view
  on(FetchQuestionnaireDetailsForEditViewRequest, state => ({
    ...state, editSurvey: {
      ...state.editSurvey, surveyDetails: {
        details: {}, loading: true
      }
    }
  })),

  on(FetchQuestionnaireDetailsForEditViewSuccess, (state, props) => ({
    ...state, editSurvey: {
      ...state.editSurvey, surveyDetails: {
        details: props.payload.surveyDetails, loading: false
      }
    }
  })),

  on(FetchQuestionnaireDetailsForEditViewFailure, state => ({
    ...state, editSurvey: {
      ...state.editSurvey, surveyDetails: {
        details: {}, loading: false
      }
    }
  })),

//edit survey for the edit screen
  on(EditSurveyDataForEditScreenRequest, state => ({
    ...state, editSurvey: {
      ...state.editSurvey, surveyDetails: {
        details: {}, loading: false
      }
    }
  })),

  on(EditSurveyDataForEditScreenSuccess, (state, props) => {
    return ({
      ...state, editSurvey: {
        ...state.editSurvey, surveyDetails: {details: props.payload.surveyDetails, loading: false}
      }
    })
  }),

  on(EditSurveyDataForEditScreenFailure, state => ({...state})),

//close survey
  on(CloseSurveyRequest, state => state),

  on(CloseSurveySuccess, state => state),

  on(CloseSurveyFailure, state => state),

//add survey response
  on(AddSurveyResponseRequest, state => state),

  on(AddSurveyResponseSuccess, state => state),

  on(AddSurveyResponseFailure, state => state),

//fetch survey details for super admin
  on(FetchSurveyResponseForReviewAndEditForSuperAdminRequest, (state, props) => ({
    ...state, surveyResponseReview: {
      surveyReviewDetails: initialState.surveyResponseReview.surveyReviewDetails, loading: true
    }
  })),

  on(FetchSurveyResponseForReviewAndEditForSuperAdminSuccess, (state, props) => ({
    ...state, surveyResponseReview: {
      surveyReviewDetails: {...props.payload.surveyDetails}, loading: false
    }
  })),

  on(FetchSurveyResponseForReviewAndEditForSuperAdminFailure, (state, props) => ({
    ...state, surveyResponseReview: {
      surveyReviewDetails: initialState.surveyResponseReview.surveyReviewDetails, loading: false
    }
  })),

// FETCH PENDING SURVEY COUNT
  on(FetchPendingSurveyCountRequest, (state) => ({
    ...state,
    pendingSurveyCount: {
      count: 0,
      loading: true
    }
  })),
  on(FetchPendingSurveyCountSuccess, (state, props) => ({
    ...state,
    pendingSurveyCount: {
      count: props.payload.count,
      loading: false
    }
  })),
  on(FetchPendingSurveyCountFailure, (state) => ({
    ...state,
    pendingSurveyCount: {
      count: 0,
      loading: false
    }
  })),

// CLEAR STATE
  on(ClearState, () => initialState)
);
